import { CalendarDays, GraduationCap, Flag, Sun } from "lucide-react";

const FAL = "#FB923C";
const RIN = "#38BDF8";

const MON = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const WD = ["søndag", "mandag", "tirsdag", "onsdag", "torsdag", "fredag", "lørdag"];

const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const fmtDay = (s) => { const d = parse(s); return `${d.getDate()}. ${MON[d.getMonth()]} ${d.getFullYear()}`; };
const weekday = (s) => WD[parse(s).getDay()];
const daysBetween = (a, b) => Math.round((parse(b) - parse(a)) / 86400000) + 1;

function fmtRange(from, to) {
  const a = parse(from), b = parse(to);
  if (from === to) return fmtDay(from);
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear())
    return `${a.getDate()}.–${b.getDate()}. ${MON[a.getMonth()]} ${a.getFullYear()}`;
  const ay = a.getFullYear() !== b.getFullYear() ? ` ${a.getFullYear()}` : "";
  return `${a.getDate()}. ${MON[a.getMonth()]}${ay} – ${b.getDate()}. ${MON[b.getMonth()]} ${b.getFullYear()}`;
}

const HELLIGDAGE = [
  { date: "2026-01-01", name: "Nytårsdag" },
  { date: "2026-04-02", name: "Skærtorsdag" },
  { date: "2026-04-03", name: "Langfredag" },
  { date: "2026-04-05", name: "Påskedag" },
  { date: "2026-04-06", name: "2. påskedag" },
  { date: "2026-05-14", name: "Kristi himmelfartsdag" },
  { date: "2026-05-24", name: "Pinsedag" },
  { date: "2026-05-25", name: "2. pinsedag" },
  { date: "2026-06-05", name: "Grundlovsdag", note: "ofte lukket / reduceret" },
  { date: "2026-12-24", name: "Juleaften", note: "ofte reduceret" },
  { date: "2026-12-25", name: "1. juledag" },
  { date: "2026-12-26", name: "2. juledag" },
  { date: "2026-12-31", name: "Nytårsaftensdag", note: "ofte reduceret" },
];

const SKOLEFERIER = [
  {
    store: "falster", color: FAL, label: "Falster", kommune: "Guldborgsund Kommune",
    periods: [
      { name: "Vinterferie", tag: "uge 8", from: "2026-02-16", to: "2026-02-20" },
      { name: "Påskeferie", from: "2026-04-02", to: "2026-04-06" },
      { name: "Kr. himmelfart", from: "2026-05-14", to: "2026-05-15" },
      { name: "2. pinsedag", from: "2026-05-25", to: "2026-05-25" },
      { name: "Sommerferie", from: "2026-06-27", to: "2026-08-09" },
      { name: "Fridag", from: "2026-09-18", to: "2026-09-18" },
      { name: "Efterårsferie", tag: "uge 42", from: "2026-10-12", to: "2026-10-16" },
      { name: "Fridag", from: "2026-11-06", to: "2026-11-06" },
      { name: "Juleferie", from: "2026-12-21", to: "2027-01-03" },
    ],
  },
  {
    store: "ringsted", color: RIN, label: "Ringsted", kommune: "Ringsted Kommune",
    periods: [
      { name: "Vinterferie", tag: "uge 7", from: "2026-02-09", to: "2026-02-13" },
      { name: "Påskeferie", from: "2026-03-30", to: "2026-04-06" },
      { name: "Kr. himmelfart", from: "2026-05-14", to: "2026-05-15" },
      { name: "2. pinsedag", from: "2026-05-25", to: "2026-05-25" },
      { name: "Sommerferie", from: "2026-06-29", to: "2026-08-10" },
      { name: "Efterårsferie", tag: "uge 42", from: "2026-10-12", to: "2026-10-16" },
      { name: "Juleferie", from: "2026-12-21", to: "2027-01-03" },
    ],
  },
];

function statusOf(from, to) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const a = parse(from), b = parse(to);
  if (now >= a && now <= b) return "active";
  if (a > now) return "upcoming";
  return "past";
}

function StatusBadge({ status, isNext }) {
  if (status === "active") return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 border border-emerald-500/30" data-testid="badge-active">Nu</span>;
  if (isNext) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-600 border border-sky-500/30" data-testid="badge-next">Næste</span>;
  return null;
}

export function HolidayOverview() {
  return (
    <div className="space-y-4" data-testid="holiday-overview">
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="holiday-header">
        <CalendarDays className="w-4 h-4" />
        <span>Ferie- og helligdagsoversigt 2026 · til planlægning · skoleferier pr. kommune</span>
      </div>

      {/* Helligdage / lukkedage */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="holiday-public">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-2"><Flag className="w-4 h-4" /> Helligdage & lukkedage</h3>
        <p className="text-xs text-slate-500 mb-4">Gælder begge butikker. Grundlovsdag, juleaften og nytårsaftensdag er ikke helligdage, men typisk lukket eller reduceret åbningstid.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {HELLIGDAGE.map((h) => {
            const status = statusOf(h.date, h.date);
            return (
              <div key={h.date} className={`rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-3 ${status === "past" ? "opacity-45" : ""}`} data-testid={`holiday-day-${h.date}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-800">{h.name}</span>
                  {status === "active" && <StatusBadge status={status} />}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{fmtDay(h.date)} · {weekday(h.date)}</div>
                {h.note && <div className="text-[11px] text-amber-600/80 mt-1">{h.note}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Skoleferier pr. butik */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SKOLEFERIER.map((s) => {
          const nextIdx = s.periods.findIndex((p) => statusOf(p.from, p.to) !== "past");
          return (
            <div key={s.store} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid={`holiday-school-${s.store}`}>
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /> Skoleferier · {s.label}
              </h3>
              <p className="text-xs text-slate-500 mb-4">{s.kommune}</p>
              <ul className="space-y-2.5">
                {s.periods.map((p, i) => {
                  const status = statusOf(p.from, p.to);
                  const isNext = i === nextIdx && status === "upcoming";
                  const days = daysBetween(p.from, p.to);
                  return (
                    <li key={`${p.name}-${p.from}`} className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ${status === "past" ? "opacity-45 border-[#e2e8f0] bg-[#f8fafc]" : status === "active" ? "border-emerald-500/40 bg-emerald-500/5" : isNext ? "border-sky-500/40 bg-sky-500/5" : "border-[#e2e8f0] bg-[#f8fafc]"}`} data-testid={`holiday-period-${s.store}-${p.from}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Sun className="w-3.5 h-3.5 flex-shrink-0" style={{ color: s.color }} />
                          <span className="text-sm font-medium text-slate-800 truncate">{p.name}</span>
                          {p.tag && <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-[#e2e8f0] rounded px-1.5 py-0.5">{p.tag}</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{fmtRange(p.from, p.to)} · {days} {days === 1 ? "dag" : "dage"}</div>
                      </div>
                      <StatusBadge status={status} isNext={isNext} />
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-500">Kilder: Ringsted Kommune og Guldborgsund Kommune officielle feriekalendere samt danske helligdage 2026. Skoleferier kan afvige lidt fra år til år — tjek kommunens hjemmeside ved tvivl.</p>
    </div>
  );
}
